/**
 * Decline application modal — distinct from cancel.
 *
 * Cancel = merchant pulled out themselves; Decline = NeptunePay rejected
 * them on compliance/risk grounds. Both are terminal but the audit
 * narrative differs — and the merchants list page can filter by
 * `?status=declined` to surface them later.
 *
 * Requires both a forensic category (dropdown) AND a >=20-char reason
 * (textarea). Both are audit-logged with the actor's identity.
 */
import React, { useState } from "react";
import { ShieldAlert, Loader2, X } from "lucide-react";
import api from "../../../lib/api";

const REASON_MIN = 20;
const CATEGORIES = [
  { value: "kyc_fail", label: "KYC failure — ID/UBO documents couldn't be verified" },
  { value: "risk", label: "Risk score too high (vertical / geography / history)" },
  { value: "sanctions", label: "Sanctions / PEP screening match" },
  { value: "non_response", label: "Non-response — merchant ghosted us" },
  { value: "vertical_not_supported", label: "Vertical not supported by policy" },
  { value: "duplicate_application", label: "Duplicate application" },
  { value: "other", label: "Other (use sparingly — prefer a specific category)" },
];


export default function DeclineMerchantModal({ merchantId, legalName, onClose, onDeclined }) {
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = !!category && reason.trim().length >= REASON_MIN && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      await api.post(`/admin/merchants/${merchantId}/decline`, {
        category, reason: reason.trim(),
      });
      onDeclined && onDeclined();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't decline this application.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4" onClick={onClose}>
      <div
        data-testid="decline-merchant-modal"
        className="w-full max-w-xl rounded-2xl border border-rose-400/15 bg-[#111] p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-rose-300" />
            <h3 className="text-[15px] font-semibold text-white">Decline application</h3>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="decline-close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <p className="mt-3 text-[12.5px] text-zinc-400 leading-relaxed">
          Marks <strong className="text-white">{legalName || merchantId}</strong> as <strong className="text-rose-300">declined</strong> &mdash; a terminal status with its own audit narrative (separate from cancelled). All data is preserved for AML retention. You can filter the merchants list by status=declined to find them later.
        </p>

        <div className="mt-4">
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Reason category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="decline-category"
            className="mt-1 w-full rounded-md border border-white/[0.08] bg-black/40 px-3 py-2 text-[12.5px] text-white focus:outline-none focus:border-rose-400/50"
          >
            <option value="">— Pick a category —</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            Reason (min {REASON_MIN} characters)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 2000))}
            placeholder="Be specific. This is preserved for audit and may surface in compliance review."
            rows={5}
            data-testid="decline-reason"
            className="mt-1 w-full rounded-md border border-white/[0.08] bg-black/40 px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-rose-400/50 resize-none"
          />
          <div className={`mt-1 text-[10.5px] ${reason.length < REASON_MIN ? "text-amber-300" : "text-zinc-500"}`}>
            {reason.length}/{REASON_MIN} minimum
          </div>
        </div>

        {error && <div className="mt-3 text-[11.5px] text-rose-300" data-testid="decline-error">{error}</div>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white">Cancel</button>
          <button
            type="button" onClick={submit} disabled={!canSubmit}
            data-testid="decline-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/85 hover:bg-rose-500 text-white px-4 py-1.5 text-[12.5px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldAlert className="h-3 w-3" />}
            Decline application
          </button>
        </div>
      </div>
    </div>
  );
}
